// src/pages/Workforce/PayrollDashboard.jsx
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from '../../api/axiosConfig';
import DataTable from '../../components/common/DataTables'; // 🟢 IMPORTED DATATABLE
import PageLayout, { PageHeader, StatsGrid, TableCard } from '../../components/common/PageLayout';
import {
  BanknotesIcon, CalendarDaysIcon, CheckCircleIcon, 
  ArrowPathIcon, CalculatorIcon, LockClosedIcon,
  DocumentArrowDownIcon
} from "@heroicons/react/24/outline";

const getLastMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

export default function PayrollDashboard() {
  const [month, setMonth] = useState(getLastMonth());
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isFinalized = useMemo(() => {
    return payrollData.length > 0 && payrollData[0].status === 'Finalized';
  }, [payrollData]);

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const res = await api.get('/workforce/payroll/summary', { params: { month } });
      const formattedData = (res.data.payroll || []).map(emp => ({
        ...emp,
        bonus: parseFloat(emp.bonus || 0).toFixed(2),
        deductions: parseFloat(emp.deductions || 0).toFixed(2),
      }));
      setPayrollData(formattedData);
      
      if (formattedData.length > 0 && formattedData[0].status === 'Finalized') {
        toast.info("This month's payroll is already finalized and locked.");
      } else {
        toast.success("Draft generated successfully.");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load payroll data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayroll(); }, [month]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleInputChange = (employeeId, field, value) => {
    if (isFinalized) return;
    setPayrollData(prev => prev.map(emp => {
      if (emp.employee_id === employeeId) {
        const updatedEmp = { ...emp, [field]: value };
        const base = parseFloat(updatedEmp.base_pay || 0);
        const ot = parseFloat(updatedEmp.overtime_pay || 0);
        const bonus = parseFloat(updatedEmp.bonus || 0);
        const ded = parseFloat(updatedEmp.deductions || 0);
        updatedEmp.net_pay = ((base + ot + bonus) - ded).toFixed(2);
        return updatedEmp;
      }
      return emp;
    }));
  };

  const handleFinalize = async () => {
    if (!window.confirm(`Are you sure you want to finalize the payroll for ${month}? This will lock the records and apply costs to the assigned projects.`)) return;
    setSaving(true);
    try {
      const payload = { month, records: payrollData };
      const res = await api.post('/workforce/payroll/finalize', payload);
      toast.success(res.data.message || "Payroll Finalized Successfully!");
      fetchPayroll();
    } catch (err) { toast.error(err.response?.data?.error || "Failed to finalize payroll"); } finally { setSaving(false); }
  };

  const totals = useMemo(() => {
    return payrollData.reduce((acc, curr) => ({
      employees: acc.employees + 1,
      gross: acc.gross + parseFloat(curr.base_pay || 0) + parseFloat(curr.overtime_pay || 0) + parseFloat(curr.bonus || 0),
      deductions: acc.deductions + parseFloat(curr.deductions || 0),
      net: acc.net + parseFloat(curr.net_pay || 0),
    }), { employees: 0, gross: 0, deductions: 0, net: 0 });
  }, [payrollData]);

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n);

  // 🟢 DATATABLE COLUMNS
  const payrollColumns = useMemo(() => [
    {
      header: "Employee",
      accessor: "name",
      render: (emp) => (
        <div>
          <p className="font-bold text-gray-900">{emp.name}</p>
          <p className="text-xs text-gray-500">{emp.department}</p>
        </div>
      )
    },
    {
      header: "Type",
      align: "center",
      render: (emp) => (
        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide ${emp.pay_type === 'Hourly' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
          {emp.pay_type}
        </span>
      )
    },
    {
      header: "Att. / Leave",
      align: "center",
      render: (emp) => (
        <div>
          <p className="font-bold text-gray-700">{emp.days_present} Days</p>
          <p className="text-[10px] font-bold">
            {emp.paid_leaves > 0 && <span className="text-green-600">{emp.paid_leaves} PL </span>}
            {emp.unpaid_absences > 0 && <span className="text-red-500">{emp.unpaid_absences} UL</span>}
          </p>
        </div>
      )
    },
    { header: "Base Earned", align: "right", cellClassName: "font-bold text-gray-700", render: (emp) => fmt(emp.base_pay) },
    { header: "Overtime", align: "right", cellClassName: "font-bold text-gray-700", render: (emp) => fmt(emp.overtime_pay) },
    {
      header: "Bonus (+)",
      align: "right",
      render: (emp) => isFinalized ? (
        <span className="text-green-600 font-bold">{fmt(emp.bonus)}</span>
      ) : (
        <input 
          type="number" min="0" value={emp.bonus} 
          onChange={(e) => handleInputChange(emp.employee_id, 'bonus', e.target.value)}
          className="w-20 sm:w-24 text-right p-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 text-green-700 font-bold outline-none"
        />
      )
    },
    {
      header: "Deductions (-)",
      align: "right",
      render: (emp) => isFinalized ? (
        <span className="text-red-600 font-bold">{fmt(emp.deductions)}</span>
      ) : (
        <input 
          type="number" min="0" value={emp.deductions} 
          onChange={(e) => handleInputChange(emp.employee_id, 'deductions', e.target.value)}
          className="w-20 sm:w-24 text-right p-1 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 text-red-700 font-bold outline-none"
        />
      )
    },
    {
      header: "Net Pay",
      align: "right",
      cellClassName: "bg-gray-50/50 rounded-r-lg",
      render: (emp) => <span className="text-base sm:text-lg font-black text-gray-900">{fmt(emp.net_pay)}</span>
    }
  ], [isFinalized]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const statsList = useMemo(() => [
    { label: 'Gross Payroll', value: fmt(totals.gross), icon: BanknotesIcon, color: 'blue' },
    { label: 'Total Deductions', value: fmt(totals.deductions), icon: DocumentArrowDownIcon, color: 'red' },
    { label: 'Net Payout', value: fmt(totals.net), icon: CheckCircleIcon, color: 'green' },
  ], [totals]);

  return (
    <PageLayout>
      
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <PageHeader
        icon={CalculatorIcon}
        iconColor="text-[#E8612C]"
        title="Run Payroll Engine"
        subtitle="Review attendance, adjust bonuses, and finalize monthly payouts."
        onRefresh={fetchPayroll}
        loading={loading}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white dark:bg-[#161b22] px-3 py-1.5 rounded-xl border border-gray-200 dark:border-[#30363d] shadow-2xs">
              <CalendarDaysIcon className="w-4 h-4 text-gray-400 dark:text-slate-500" />
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="text-xs font-semibold text-gray-700 dark:text-slate-200 bg-transparent outline-none cursor-pointer"
              />
            </div>
            <button
              onClick={handleFinalize}
              disabled={isFinalized || saving || payrollData.length === 0}
              className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 transition shadow-2xs ${
                isFinalized
                  ? 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 border border-gray-200 dark:border-slate-700 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/20'
              }`}
            >
              {isFinalized ? <LockClosedIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
              {saving ? 'Processing...' : isFinalized ? 'Payroll Locked' : 'Finalize & Lock'}
            </button>
          </div>
        }
      />

      {/* ── STATS ───────────────────────────────────────────────────────────── */}
      <StatsGrid stats={statsList} cols={3} />

      {/* ── DATATABLE INTEGRATION ───────────────────────────────────────────── */}
      <TableCard>
        <DataTable 
          data={payrollData} 
          columns={payrollColumns} 
          keyExtractor={(item) => item.employee_id}
          emptyMessage="No active employees found or data could not be calculated."
          loading={loading}
        />
      </TableCard>
    </PageLayout>
  );
}